import * as THREE from 'three'
import {
  ESPESSURA_PAINEL_TETO,
  Y_BASE_LATERAL,
  alturaBaseVaoPorta,
  extremosTampaTraseira,
  montanteColunaC,
  raioArcoRoda,
  secaoColuna,
  yTopoLateral,
  zDaColunaNaAltura,
  type FaixaLongitudinal,
  type PerfilVeiculo,
} from '../../dominio/didatico/perfilVeiculo'

/**
 * Silhueta lateral da carroceria, para `ExtrudeGeometry`.
 *
 * É uma chapa única no plano ZY com:
 *  - os arcos de roda entalhados na base;
 *  - os vãos das portas recortados como furos.
 *
 * Caixas simples não dão silhueta de automóvel; este contorno dá — e continua
 * sendo geometria procedural, sem asset externo.
 *
 * Coordenadas da forma: X = z do veículo, Y = y do veículo. A malha é girada
 * ao ser instanciada para que a extrusão aponte para a lateral do veículo.
 */
/** Quanto o paralama termina antes da ponta do veículo, em metros. */
export const FOLGA_PARALAMA_FRENTE = 0.08

/** O mesmo recuo atrás: a ponta da traseira é do para-choque e da tampa, não do paralama. */
export const FOLGA_PARALAMA_TRASEIRA = 0.08

export function formaLateralCarroceria(perfil: PerfilVeiculo): THREE.Shape {
  const forma = new THREE.Shape()
  const r = raioArcoRoda(perfil)
  const yBase = Y_BASE_LATERAL
  const yTopo = yTopoLateral(perfil)
  const yEixo = perfil.raioRoda
  const anguloBase = Math.asin((yBase - yEixo) / r)
  const meiaAbertura = Math.sqrt(r * r - (yBase - yEixo) ** 2)
  const baseDianteira = alturaBaseVaoPorta(perfil, perfil.portaDianteira)
  const baseTraseira = alturaBaseVaoPorta(perfil, perfil.portaTraseira)
  const { frenteZ, eixoDianteiroZ, eixoTraseiroZ, capoY } = perfil
  const zFaceTraseira = perfil.traseiraZ + FOLGA_PARALAMA_TRASEIRA

  // base, da traseira para a frente, entalhando os dois arcos de roda
  forma.moveTo(zFaceTraseira + 0.14, yBase)
  forma.lineTo(eixoTraseiroZ - meiaAbertura, yBase)
  forma.absarc(eixoTraseiroZ, yEixo, r, Math.PI - anguloBase, anguloBase, true)
  forma.lineTo(eixoDianteiroZ - meiaAbertura, yBase)
  forma.absarc(eixoDianteiroZ, yEixo, r, Math.PI - anguloBase, anguloBase, true)
  // extremidade dianteira e linha do capô. O paralama termina rente ao painel
  // frontal (grade e faróis), não na ponta do veículo: antes ele avançava até
  // `frenteZ`, 9 cm à frente da grade, e virava uma orelha alta de cada lado. A
  // ponta é do para-choque, que envolve a frente e é a única peça que a alcança.
  const zFace = frenteZ - FOLGA_PARALAMA_FRENTE
  forma.lineTo(zFace - 0.14, yBase)
  forma.quadraticCurveTo(zFace, yBase, zFace, yBase + 0.14)
  forma.lineTo(zFace, capoY - 0.14)
  forma.quadraticCurveTo(zFace, capoY, zFace - 0.2, capoY)
  forma.lineTo(perfil.colunaA.zBase + 0.06, capoY)

  // recorte aberto das portas: ao retirar a porta, o vão fica totalmente livre
  // sem nenhuma barra horizontal residual na linha de cintura
  const zB_frente = perfil.portaDianteira.zTraseiro + 0.025
  const zB_tras = perfil.portaTraseira.zDianteiro - 0.025

  // batente dianteiro descendo da cintura até o topo da soleira
  forma.lineTo(perfil.portaDianteira.zDianteiro, perfil.cinturaY)
  forma.lineTo(perfil.portaDianteira.zDianteiro, baseDianteira)
  // soleira sob o vão da porta dianteira
  forma.lineTo(zB_frente, baseDianteira)
  // cobertura externa da coluna B entre as duas portas
  forma.lineTo(zB_frente, perfil.cinturaY)
  forma.lineTo(zB_tras, perfil.cinturaY)
  forma.lineTo(zB_tras, baseTraseira)
  // soleira sob o vão da porta traseira
  forma.lineTo(perfil.portaTraseira.zTraseiro, baseTraseira)
  // batente traseiro subindo da soleira para a lateral traseira
  forma.lineTo(perfil.portaTraseira.zTraseiro, yTopo)
  forma.lineTo(perfil.colunaC.zBase - 0.02, yTopo)

  // volume traseiro: o hatch acompanha a tampa por baixo; o sedan desce abaixo do tampo
  if (perfil.volumeTraseiro === 'hatch') {
    const { de, para } = extremosTampaTraseira(perfil)
    const m = (para.y - de.y) / (para.z - de.z)
    const yLidOffset = -0.025
    const raioCurva = 0.04
    const zCurva = zFaceTraseira + raioCurva
    const yCurva = de.y + yLidOffset + m * (zCurva - de.z)
    const yControle = yCurva - m * raioCurva

    forma.lineTo(de.z, de.y + yLidOffset)
    forma.lineTo(zCurva, yCurva)
    forma.quadraticCurveTo(zFaceTraseira, yControle, zFaceTraseira, yControle - raioCurva)
    forma.lineTo(zFaceTraseira, yBase + 0.14)
    forma.quadraticCurveTo(zFaceTraseira, yBase, zFaceTraseira + 0.14, yBase)
  } else {
    const yAbaixoTampo = perfil.cinturaY + 0.06 - 0.025
    const raioCurva = 0.06
    forma.lineTo(perfil.colunaC.zBase - 0.02, yAbaixoTampo)
    forma.lineTo(zFaceTraseira + raioCurva, yAbaixoTampo)
    forma.quadraticCurveTo(zFaceTraseira, yAbaixoTampo, zFaceTraseira, yAbaixoTampo - raioCurva)
    forma.lineTo(zFaceTraseira, yBase + 0.14)
    forma.quadraticCurveTo(zFaceTraseira, yBase, zFaceTraseira + 0.14, yBase)
  }
  forma.closePath()

  return forma
}

export type VaoVidroLateral = 'dianteiro' | 'traseiro'

export interface ContornoVidroLateral {
  frenteBase: number
  frenteTopo: number
  trasBase: number
  trasTopo: number
  yBase: number
  yTopo: number
}

/**
 * Topo das janelas e dos painéis de coluna: encosta na face de baixo do painel do
 * teto, que fica meia espessura abaixo de `tetoY`. Antes ele parava 6 cm antes
 * (3,5 cm de vão por baixo do teto) e as colunas e vidros ficavam soltos do
 * teto, com a cabine à mostra por cima das janelas.
 */
export const yTopoDasJanelas = (perfil: PerfilVeiculo): number =>
  perfil.tetoY - ESPESSURA_PAINEL_TETO / 2 + 0.005

/** Os vidros das portas seguem os batentes; o traseiro estreita perto da coluna C. */
export function contornoVidroPorta(
  perfil: PerfilVeiculo,
  faixa: FaixaLongitudinal,
  dianteira: boolean,
): ContornoVidroLateral {
  const yBase = perfil.cinturaY + 0.025
  const yTopo = yTopoDasJanelas(perfil)
  const secaoA = secaoColuna('A')
  const secaoB = secaoColuna('B')
  const meiaLarguraB = secaoB.largura / 2

  if (dianteira) {
    const frenteTopo = zDaColunaNaAltura(perfil, perfil.colunaA, yTopo) - secaoA.largura
    const frenteBase = Math.min(
      faixa.zDianteiro - 0.05,
      zDaColunaNaAltura(perfil, perfil.colunaA, yBase) - secaoA.largura,
    )
    const trasBase = perfil.colunaB.z + meiaLarguraB
    const trasTopo = perfil.colunaB.z + meiaLarguraB
    return {
      frenteBase,
      frenteTopo,
      trasBase,
      trasTopo,
      yBase,
      yTopo,
    }
  }

  const frenteBase = perfil.colunaB.z - meiaLarguraB
  const frenteTopo = perfil.colunaB.z - meiaLarguraB
  // a janela vai até a frente da coluna C: não há vigia entre a porta e a C
  const montante = montanteColunaC(perfil)
  const meiaLarguraC = secaoColuna('C').largura / 2
  const trasBase = montante.zBase + meiaLarguraC
  const trasTopo = zDaColunaNaAltura(perfil, montante, yTopo) + meiaLarguraC
  return {
    frenteBase,
    frenteTopo,
    trasBase,
    trasTopo,
    yBase,
    yTopo,
  }
}

export function contornoVidroCabine(perfil: PerfilVeiculo, vao: VaoVidroLateral): ContornoVidroLateral {
  const frente = contornoVidroPorta(perfil, perfil.portaDianteira, true)
  const traseiro = contornoVidroPorta(perfil, perfil.portaTraseira, false)
  if (vao === 'dianteiro') return {
    ...frente,
    frenteBase: Math.min(
      perfil.colunaA.zBase - 0.035,
      zDaColunaNaAltura(perfil, perfil.colunaA, frente.yBase) - secaoColuna('A').largura,
    ),
  }
  return traseiro
}

/**
 * Chapa da coluna C: do montante (batente da porta) até a borda do vidro
 * traseiro, da cintura ao teto. É o que torna a C a coluna larga que cobre a
 * caixa de roda, e não uma barra solta sobre a roda.
 */
export function contornoChapaColunaC(perfil: PerfilVeiculo): ContornoVidroLateral {
  const montante = montanteColunaC(perfil)
  const secaoC = secaoColuna('C')
  const meiaLarguraC = secaoC.largura / 2
  const yBase = perfil.cinturaY
  const yTopo = yTopoDasJanelas(perfil)
  return {
    frenteBase: montante.zBase + meiaLarguraC + 0.01,
    frenteTopo: zDaColunaNaAltura(perfil, montante, yTopo) + meiaLarguraC + 0.01,
    trasBase: perfil.colunaC.zBase - 0.02,
    trasTopo: zDaColunaNaAltura(perfil, perfil.colunaC, yTopo) - 0.02,
    yBase,
    yTopo,
  }
}

/**
 * Painel da coluna A: o triângulo entre a coluna inclinada e a frente do vidro
 * da porta.
 *
 * O para-brisa segue a coluna A (cerca de 47° de inclinação), mas o vidro da
 * porta nasce na frente da própria porta. Entre os dois sobrava um vão aberto,
 * de uns 26 cm no pé da coluna afinando até alguns centímetros no teto. Num
 * carro de verdade esse triângulo é a moldura da porta e o painel da coluna;
 * aqui ele é um painel pintado preso à carroceria — não à porta, que gira em
 * torno da dobradiça —, então continua no lugar com a porta aberta ou retirada.
 */
export function contornoChapaColunaA(perfil: PerfilVeiculo): ContornoVidroLateral {
  const vidro = contornoVidroPorta(perfil, perfil.portaDianteira, true)
  return {
    frenteBase: zDaColunaNaAltura(perfil, perfil.colunaA, vidro.yBase),
    frenteTopo: zDaColunaNaAltura(perfil, perfil.colunaA, vidro.yTopo),
    // sobrepõe 1 cm no vidro: sem isso a junção deixa uma fresta de luz
    trasBase: vidro.frenteBase - 0.01,
    trasTopo: vidro.frenteTopo - 0.01,
    yBase: vidro.yBase,
    yTopo: vidro.yTopo,
  }
}

/**
 * Painel da coluna B: o que fecha o vão entre o vidro da porta dianteira e o da
 * traseira, da cintura ao teto.
 *
 * Os dois vidros param nos batentes das portas e entre eles sobram uns 10 cm
 * abertos — pela fresta via-se a cabine inteira. A coluna B de verdade é uma
 * chapa ali; este painel é preso à carroceria, não às portas, e fica no lugar
 * com elas abertas ou retiradas.
 */
export function contornoChapaColunaB(perfil: PerfilVeiculo): ContornoVidroLateral {
  const dianteira = contornoVidroPorta(perfil, perfil.portaDianteira, true)
  const traseira = contornoVidroPorta(perfil, perfil.portaTraseira, false)
  return {
    // entra 1 cm em cada vidro: sem isso a junção deixa uma fresta de luz
    frenteBase: dianteira.trasBase + 0.01,
    frenteTopo: dianteira.trasTopo + 0.01,
    trasBase: traseira.frenteBase - 0.01,
    trasTopo: traseira.frenteTopo - 0.01,
    yBase: dianteira.yBase,
    yTopo: dianteira.yTopo,
  }
}

/** Divisões reais da lateral: janela dianteira e traseira; a C é chapa, sem vidro. */
export function formaVidroLateral(contorno: ContornoVidroLateral): THREE.Shape {
  const { frenteBase, frenteTopo, trasBase, trasTopo, yBase, yTopo } = contorno
  const meioY = (yBase + yTopo) / 2
  const forma = new THREE.Shape()
  forma.moveTo(frenteBase, yBase)
  forma.lineTo((frenteBase + frenteTopo) / 2, meioY)
  forma.lineTo(frenteTopo, yTopo)
  forma.lineTo(trasTopo, yTopo)
  forma.lineTo((trasBase + trasTopo) / 2, meioY)
  forma.lineTo(trasBase, yBase)
  forma.closePath()
  return forma
}

/**
 * Raio da quina da chapa extrudada.
 *
 * Aresta viva não existe em chapa estampada, e é a quina arredondada que captura
 * o realce da faixa lateral: sem ela a lateral inteira reflete com o mesmo valor
 * e volta a ler como laje. Vale também para os furos das portas, onde o bevel
 * arredonda o recorte.
 */
export const BEVEL_CHAPA = 0.012

export const OPCOES_EXTRUSAO = {
  bevelEnabled: true,
  bevelThickness: BEVEL_CHAPA,
  bevelSize: BEVEL_CHAPA,
  bevelSegments: 2,
  // os arcos de roda são o contorno mais visível da silhueta; 12 segmentos
  // deixavam a caçamba facetada
  curveSegments: 20,
} as const

